import React from 'react';
import { Button, Input, Checkbox, ControlLink, Overlay, Text } from '@moda/om';
import { noop } from '../../lib/noop';
import { useForm } from '../../hooks/useForm';
import { AuthAction } from '../../lib/authenticate';
import { SocialAuthProvider } from '../../hooks/useSocialAuth';
import { AuthPayload } from '../../lib/authenticate';
import { LoadingBalls } from '../LoadingBalls';
import { useAuth } from '../../hooks/useAuth';
import { SocialAuthButton } from './SocialAuthButton';
import { AccountLocked } from './AccountLocked';

type SignInProps = {
  title?: string;
  message?: string;
  className: string;
  onAuth: (authUser?: AuthPayload) => void;
  onClickForgotPassword?: () => void;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
};

type SignInFormValues = {
  signInEmail: string;
  signInPassword: string;
  signInRemember: boolean;
};

const SIGN_IN_TITLE = 'Have an Account?';
const SIGN_IN_MESSAGE =
  'Sign in to speed up the checkout process, check your order status and save your favorite products.';
const SIGN_IN_SUBMIT = 'SIGN IN';
export const FORGOT_PASSWORD_TEXT = 'Forgot Password?';

const validations = {
  signInEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  },
  signInPassword: {
    required: {
      message: 'Invalid password, please try again.',
      value: true
    }
  }
};

export const SignIn: React.FC<SignInProps> = ({
  title = SIGN_IN_TITLE,
  message = SIGN_IN_MESSAGE,
  className,
  showAuthModal,
  hideAuthModal,
  onAuth,
  onClickForgotPassword
}) => {
  const { error: errorAuth, handleAuth, loading } = useAuth(AuthAction.SignIn);
  const onSubmit = (values: SignInFormValues) => {
    handleAuth({
      email: values.signInEmail,
      password: values.signInPassword,
      remember: values.signInRemember
    })
      .then(onAuth)
      .catch(noop);
  };
  const { errors: fieldErrors, handleSubmit } = useForm({ onSubmit, validations });

  return (
    <>
      <div className={className} data-testid="signInSection">
        <form onSubmit={handleSubmit}>
          <div className="Authentication__title">{title}</div>
          <div className="Authentication__message">{message}</div>
          <div className="Authentication__error">
            {errorAuth?.code === 'account_locked' ? <AccountLocked /> : errorAuth?.message}
          </div>

          <div className="Authentication__fields">
            <Input
              className="Authentication__input"
              error={fieldErrors.signInEmail?.message || errorAuth != null}
              id="signInEmail"
              data-testid="signInEmail"
              placeholder="Email"
              type="email"
            />

            <Input
              className="Authentication__input"
              error={fieldErrors.signInPassword?.message || errorAuth != null}
              id="signInPassword"
              data-testid="signInPassword"
              placeholder="Password"
              type="password"
            />

            <div className="Authentication__bottomFields">
              <div className="Authentication__rememberMe">
                <Checkbox
                  className="Authentication__checkbox"
                  defaultChecked
                  id="signInRemember"
                  data-testid="signInRemember"
                  type="checkbox"
                >
                  Remember me
                </Checkbox>
              </div>

              <div className="Authentication__forgotPassword" data-testid="signInForgotPassword">
                {onClickForgotPassword ? (
                  <ControlLink onClick={onClickForgotPassword} type="button">
                    {FORGOT_PASSWORD_TEXT}
                  </ControlLink>
                ) : (
                  <ControlLink to="/forgot-password">{FORGOT_PASSWORD_TEXT}</ControlLink>
                )}
              </div>
            </div>
          </div>

          <Button className="Authentication__button" type="submit" data-testid="signInSubmit">
            {SIGN_IN_SUBMIT}
          </Button>
        </form>

        <div className="Authentication__divider" />

        <SocialAuthButton
          action={AuthAction.SignIn}
          provider={SocialAuthProvider.Amazon}
          showAuthModal={showAuthModal}
          hideAuthModal={hideAuthModal}
          onAuth={onAuth}
        />
        <SocialAuthButton
          action={AuthAction.SignIn}
          provider={SocialAuthProvider.Facebook}
          showAuthModal={showAuthModal}
          hideAuthModal={hideAuthModal}
          onAuth={onAuth}
        />
        <div className="Authentication__privacyPolicy">
          <ControlLink to="/privacy" rel="noopener noreferrer" target="_blank">
            Privacy Policy
          </ControlLink>
        </div>
      </div>

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />
        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
